import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';

const FILE_ACCEPT = '.pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.gif,.webp,.txt,.zip,.7z,.psd,.ai,.cdr,.mp4,.mov';
const NO_POINT_BLOCKS = new Set(['social', 'novelties']);
const DRAFT_STORAGE_KEY = 'helpdesk_create_ticket_draft_v1';

const BLOCKS = [
  { key: 'tech', icon: '💻', name: 'Тех. работа', subtypes: ['Оборудование', 'Интернет', 'Другое'] },
  { key: 'design', icon: '🎨', name: 'Дизайн', subtypes: ['Полиграфия', 'Мерч/брендинг', 'Веб-макеты', 'Другое'] },
  { key: 'loyalty', icon: '🎁', name: 'Программа лояльности', subtypes: ['Акция Tabakov Shop', 'Акция Hookah-Service', 'Технические проблемы', 'Другое'] },
  { key: 'video', icon: '🎬', name: 'Видео', subtypes: ['Ролик для ТВ', 'Ролик для соцсетей', 'Другое'] },
  { key: 'social', icon: '📱', name: 'Социальные сети', subtypes: ['Пост', 'Другое'] },
  { key: 'novelties', icon: '🆕', name: 'Новинки', subtypes: ['Кальянные смеси', 'Кальяны', 'Уголь', 'Кальянные аксы', 'POD-системы', 'Одноразки', 'Жидкости', 'ЖТ', 'Другое'] },
];

function fileName(file) {
  if (!file) return 'file';
  if (typeof file === 'string') return file.split('/').pop();
  return file.name || file.filename || 'file';
}

function isImageFile(file) {
  const name = fileName(file);
  const type = typeof file === 'object' ? file.content_type : '';
  return /^image\//.test(type || '') || /\.(jpg|jpeg|png|gif|webp)$/i.test(name);
}

function activeOnly(items = []) {
  return items.filter(item => item.is_active !== false);
}

function pointKey(network, point) {
  return `${network.slug}:${point.id}`;
}

function flattenNetworkPoints(network) {
  const zonePoints = activeOnly(network.zones).flatMap(zone =>
    activeOnly(zone.points).map(point => ({ ...point, zone_name: zone.name }))
  );
  return [...zonePoints, ...activeOnly(network.points)];
}

function networkMatchesUser(user, network) {
  if (!user) return false;
  if (['admin', 'company_head', 'retail_manager', 'marketer'].includes(user.role)) return true;
  if (user.role === 'admin_retail_hs') return network.slug === 'hookah';
  if (user.role === 'admin_retail_ts') return network.slug.startsWith('tabakov');
  if (user.network === 'all') return true;
  return user.network === network.slug;
}

function filterPointsForUser(user, network, points) {
  if (!user) return [];
  if (!user.primary_point || user.primary_point === 'all') return points;
  if (['admin', 'company_head', 'retail_manager', 'marketer', 'admin_retail_hs', 'admin_retail_ts'].includes(user.role)) {
    return points;
  }
  return points.filter(point => point.name === user.primary_point);
}

function filterNetworksForTicket(user, networks, block, subtype) {
  let allowed = activeOnly(networks)
    .filter(network => networkMatchesUser(user, network))
    .map(network => ({
      ...network,
      flatPoints: filterPointsForUser(user, network, flattenNetworkPoints(network)),
    }))
    .filter(network => network.flatPoints.length > 0);

  if (block === 'loyalty' && subtype === 'Акция Hookah-Service') {
    allowed = allowed.filter(network => network.slug === 'hookah');
  }
  if (block === 'loyalty' && subtype === 'Акция Tabakov Shop') {
    allowed = allowed.filter(network => network.slug === 'tabakov_mgn');
  }

  const order = ['hookah', 'tabakov_mgn', 'tabakov_chel'];
  return allowed.sort((a, b) => {
    const ai = order.indexOf(a.slug);
    const bi = order.indexOf(b.slug);
    return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
  });
}

function selectedPointNames(selectedPointIds, networks) {
  const selected = new Set(selectedPointIds);
  const names = [];
  networks.forEach(network => {
    network.flatPoints.forEach(point => {
      if (selected.has(pointKey(network, point))) names.push(point.name);
    });
  });
  return names;
}

function detectNetwork(selectedPointIds, networks) {
  const selected = new Set(selectedPointIds);
  const slugs = new Set();
  networks.forEach(network => {
    if (network.flatPoints.some(point => selected.has(pointKey(network, point)))) {
      slugs.add(network.slug);
    }
  });
  return slugs.size === 1 ? [...slugs][0] : null;
}

function networkAccent(slug) {
  if (slug === 'hookah') return 'network-accent-hookah';
  if (slug?.startsWith('tabakov')) return 'network-accent-tabakov';
  return '';
}

export default function CreateTicket() {
  const navigate = useNavigate();
  const fileRef = useRef();
  const [step, setStep] = useState(1);
  const [user, setUser] = useState(null);
  const [networks, setNetworks] = useState([]);
  const [expandedNetworks, setExpandedNetworks] = useState({});
  const [contextLoading, setContextLoading] = useState(true);
  const [contextError, setContextError] = useState('');
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState('');

  const [block, setBlock] = useState('');
  const [subtype, setSubtype] = useState('');
  const [selectedPointIds, setSelectedPointIds] = useState([]);
  const [description, setDescription] = useState('');
  const [files, setFiles] = useState([]);
  const [priority, setPriority] = useState('normal');
  const [deadline, setDeadline] = useState('');

  useEffect(() => {
    try {
      const draft = JSON.parse(localStorage.getItem(DRAFT_STORAGE_KEY) || 'null');
      if (!draft) return;
      setBlock(draft.block || '');
      setSubtype(draft.subtype || '');
      setSelectedPointIds(draft.selectedPointIds || []);
      setDescription(draft.description || '');
      setPriority(draft.priority || 'normal');
      setDeadline(draft.deadline || '');
    } catch {
      localStorage.removeItem(DRAFT_STORAGE_KEY);
    }
  }, []);

  const loadContext = async () => {
    setContextLoading(true);
    setContextError('');
    try {
      const [meResponse, networksResponse] = await Promise.all([
        api.get('/api/app/me'),
        api.get('/api/app/networks'),
      ]);
      setUser(meResponse.data);
      setNetworks(networksResponse.data.networks || []);
    } catch {
      setContextError('Не удалось загрузить профиль и доступные точки');
    } finally {
      setContextLoading(false);
    }
  };

  useEffect(() => { loadContext(); }, []);

  const selectedBlock = BLOCKS.find(b => b.key === block);
  const needsPoints = block && !NO_POINT_BLOCKS.has(block);
  const availableNetworks = useMemo(
    () => filterNetworksForTicket(user, networks, block, subtype),
    [user, networks, block, subtype]
  );
  const selectedNames = selectedPointNames(selectedPointIds, availableNetworks);

  useEffect(() => {
    setSelectedPointIds([]);
    setExpandedNetworks({});
  }, [block, subtype]);

  useEffect(() => {
    if (contextLoading) return;
    const draft = { block, subtype, selectedPointIds, description, priority, deadline };
    const hasDraft = Boolean(block || subtype || description || selectedPointIds.length || deadline);
    if (hasDraft) {
      localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(draft));
      window.Telegram?.WebApp?.enableClosingConfirmation?.();
    } else {
      localStorage.removeItem(DRAFT_STORAGE_KEY);
      window.Telegram?.WebApp?.disableClosingConfirmation?.();
    }
  }, [block, subtype, selectedPointIds, description, priority, deadline, contextLoading]);

  useEffect(() => () => window.Telegram?.WebApp?.disableClosingConfirmation?.(), []);

  const toggleNetworkOpen = (slug) => {
    setExpandedNetworks(prev => ({ ...prev, [slug]: !prev[slug] }));
  };

  const togglePoint = (network, point) => {
    const key = pointKey(network, point);
    setSelectedPointIds(prev =>
      prev.includes(key) ? prev.filter(item => item !== key) : [...prev, key]
    );
  };

  const toggleWholeNetwork = (network) => {
    const keys = network.flatPoints.map(point => pointKey(network, point));
    const allSelected = keys.every(key => selectedPointIds.includes(key));
    setSelectedPointIds(prev => {
      if (allSelected) return prev.filter(key => !keys.includes(key));
      return [...new Set([...prev, ...keys])];
    });
  };

  const goNextFromCategory = () => {
    if (!needsPoints) setStep(3);
    else setStep(2);
  };

  const goBackFromDescription = () => {
    if (!needsPoints) setStep(1);
    else setStep(2);
  };

  const handleFileUpload = async (e) => {
    const selectedFiles = Array.from(e.target.files || []);
    if (!selectedFiles.length) return;
    setUploading(true);
    setUploadProgress(0);
    setError('');
    try {
      const uploaded = [];
      for (let index = 0; index < selectedFiles.length; index += 1) {
        const form = new FormData();
        form.append('file', selectedFiles[index]);
        const res = await api.post('/api/app/upload', form, {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: event => {
            const fraction = event.total ? event.loaded / event.total : 0;
            setUploadProgress(Math.round(((index + fraction) / selectedFiles.length) * 100));
          },
        });
        uploaded.push(res.data);
      }
      setFiles(prev => [...prev, ...uploaded]);
    } catch {
      setError('Ошибка загрузки файла');
    } finally {
      setUploading(false);
      setUploadProgress(0);
      fileRef.current.value = '';
    }
  };

  const removeFile = (idx) => setFiles(prev => prev.filter((_, i) => i !== idx));

  const canNext1 = !!(block && subtype);
  const canNext2 = !needsPoints || selectedPointIds.length > 0;
  const canNext3 = description.trim().length > 0;

  const handleSubmit = async () => {
    if (!block || !subtype || !description.trim() || (needsPoints && selectedPointIds.length === 0)) {
      setError('Заполните все обязательные поля');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await api.post('/api/app/tickets', {
        telegram_id: user.telegram_id,
        points: needsPoints ? selectedNames : [],
        network: needsPoints ? detectNetwork(selectedPointIds, availableNetworks) : null,
        block,
        problem_type: subtype,
        description: description.trim(),
        priority,
        deadline: deadline || null,
        files,
      });
      localStorage.removeItem(DRAFT_STORAGE_KEY);
      window.Telegram?.WebApp?.disableClosingConfirmation?.();
      navigate(`/app/tickets/${res.data.id}`);
    } catch (err) {
      setError(err.response?.data?.detail || 'Ошибка создания заявки');
    } finally {
      setLoading(false);
    }
  };

  if (contextLoading) return <div className="app-loader"><div className="spinner" /><p>Загрузка...</p></div>;
  if (contextError) return (
    <div className="app-page centered">
      <div className="error-msg">{contextError}</div>
      <button className="btn-primary" onClick={loadContext}>Повторить</button>
      <button className="btn-secondary" onClick={() => navigate('/app')}>Назад</button>
    </div>
  );

  return (
    <div className="app-page">
      <div className="app-header">
        <button
          className="btn-back"
          onClick={() => {
            if (step === 1) navigate('/app');
            else if (step === 3) goBackFromDescription();
            else setStep(s => s - 1);
          }}
        >←</button>
        <h1>Новая заявка</h1>
        <div className="step-dots">
          {[1, 2, 3, 4].map(s => <div key={s} className={`step-dot ${step >= s ? 'active' : ''}`} />)}
        </div>
      </div>

      {error && <div className="error-msg">{error}</div>}

      {step === 1 && (
        <div className="app-section">
          <h2>Категория</h2>
          <div className="blocks-grid">
            {BLOCKS.map(b => (
              <div key={b.key} className={`block-card ${block === b.key ? 'selected' : ''}`} onClick={() => { setBlock(b.key); setSubtype(''); }}>
                <div className="block-icon">{b.icon}</div>
                <div className="block-name">{b.name}</div>
              </div>
            ))}
          </div>
          {block && (
            <div className="subtypes">
              <h3>Тип:</h3>
              {selectedBlock?.subtypes.map(s => (
                <label key={s} className={`radio-chip ${subtype === s ? 'selected' : ''}`}>
                  <input type="radio" name="subtype" value={s} checked={subtype === s} onChange={() => setSubtype(s)} />
                  {s}
                </label>
              ))}
            </div>
          )}
          {!needsPoints && block && subtype && (
            <div className="info-card success-card">
              Для этой категории выбор точек не нужен.
            </div>
          )}
          <button className="btn-primary full-width mt-auto" disabled={!canNext1} onClick={goNextFromCategory}>Далее →</button>
        </div>
      )}

      {step === 2 && needsPoints && (
        <div className="app-section">
          <h2>Место <span style={{ fontSize: 13, fontWeight: 400, color: 'var(--text-secondary)' }}>(можно выбрать сеть целиком или отдельные точки)</span></h2>
          <div className="network-picker">
            {availableNetworks.map(network => {
              const keys = network.flatPoints.map(point => pointKey(network, point));
              const selectedCount = keys.filter(key => selectedPointIds.includes(key)).length;
              const allSelected = keys.length > 0 && selectedCount === keys.length;
              const isOpen = expandedNetworks[network.slug] ?? selectedCount > 0;

              return (
                <div key={network.id} className={`network-picker-group ${networkAccent(network.slug)} ${allSelected ? 'selected' : ''}`}>
                  <div className="network-picker-head">
                    <button type="button" className="network-toggle" onClick={() => toggleNetworkOpen(network.slug)}>
                      <span>{isOpen ? '▾' : '▸'} {network.name}</span>
                      <small>{selectedCount}/{keys.length}</small>
                    </button>
                    <button type="button" className="btn-secondary small" onClick={() => toggleWholeNetwork(network)}>
                      {allSelected ? 'Снять выбор' : 'Вся сеть'}
                    </button>
                  </div>

                  {isOpen && (
                    <div className="network-picker-points">
                      {network.flatPoints.map(point => (
                        <label key={pointKey(network, point)} className={`point-option ${selectedPointIds.includes(pointKey(network, point)) ? 'selected' : ''}`}>
                          <input
                            type="checkbox"
                            value={pointKey(network, point)}
                            checked={selectedPointIds.includes(pointKey(network, point))}
                            onChange={() => togglePoint(network, point)}
                          />
                          <span>{point.name}</span>
                          {point.zone_name && <small>{point.zone_name}</small>}
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          {availableNetworks.length === 0 && <div className="empty-state small">Нет доступных точек для выбранного типа заявки</div>}
          {selectedNames.length > 0 && (
            <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 8 }}>
              Выбрано: {selectedNames.join(', ')}
            </div>
          )}
          <div className="step-nav">
            <button className="btn-ghost" onClick={() => setStep(1)}>← Назад</button>
            <button className="btn-primary" disabled={!canNext2} onClick={() => setStep(3)}>Далее →</button>
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="app-section">
          <h2>Описание</h2>
          <div className="form-group">
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="Подробно опишите проблему или задачу..."
              rows={5}
              maxLength={2000}
            />
            <div className="char-count">{description.length}/2000</div>
          </div>

          <div className="files-section">
            <input type="file" ref={fileRef} onChange={handleFileUpload} style={{ display: 'none' }} accept={FILE_ACCEPT} multiple />
            <button className="btn-ghost" onClick={() => fileRef.current.click()} disabled={uploading}>
              {uploading ? 'Загрузка...' : '📎 Прикрепить файл'}
            </button>
            {uploading && <div className="hint">Загрузка: {uploadProgress}%</div>}
            {files.length > 0 && (
              <div className="files-preview">
                {files.map((f, i) => (
                  <div key={i} className="file-chip">
                    {isImageFile(f) ? (
                      <span className="file-link">🖼 {fileName(f)}</span>
                    ) : (
                      <span className="file-link">📎 {fileName(f)}</span>
                    )}
                    <button className="file-remove" onClick={() => removeFile(i)}>✕</button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="step-nav">
            <button className="btn-ghost" onClick={goBackFromDescription}>← Назад</button>
            <button className="btn-primary" disabled={!canNext3} onClick={() => setStep(4)}>Далее →</button>
          </div>
        </div>
      )}

      {step === 4 && (
        <div className="app-section">
          <h2>Приоритет и срок</h2>
          <div className="form-group">
            <label>Приоритет</label>
            <div className="priority-group">
              {[
                { value: 'urgent', label: '🔴 Срочно' },
                { value: 'normal', label: '🟡 Обычно' },
                { value: 'low', label: '🟢 Не горит' },
              ].map(opt => (
                <label key={opt.value} className={`priority-chip ${priority === opt.value ? 'selected' : ''}`}>
                  <input type="radio" name="priority" value={opt.value} checked={priority === opt.value} onChange={() => setPriority(opt.value)} />
                  {opt.label}
                </label>
              ))}
            </div>
          </div>

          <div className="form-group">
            <label>Дедлайн (необязательно)</label>
            <input type="datetime-local" min={new Date().toISOString().slice(0, 16)} value={deadline} onChange={e => setDeadline(e.target.value)} />
          </div>

          <div className="summary-card">
            <div className="summary-row"><span>Категория:</span><span>{selectedBlock?.icon} {selectedBlock?.name}</span></div>
            <div className="summary-row"><span>Тип:</span><span>{subtype}</span></div>
            <div className="summary-row"><span>Место:</span><span>{needsPoints ? selectedNames.join(', ') : 'Не требуется'}</span></div>
            {files.length > 0 && <div className="summary-row"><span>Файлов:</span><span>{files.length}</span></div>}
          </div>

          <div className="step-nav">
            <button className="btn-ghost" onClick={() => setStep(3)}>← Назад</button>
            <button className="btn-primary" onClick={handleSubmit} disabled={loading}>
              {loading ? 'Создание...' : '✅ Создать заявку'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
